import { useSearchParams } from "react-router-dom";
import { IndianRupee, MessageSquare, Video, Building2, AlertTriangle } from "lucide-react";
import SEO from "@/components/SEO";
import { Reveal, Eyebrow } from "@/components/Reveal";

const WA_NUMBER = "919559758479";

const waLink = (message) =>
  `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(message)}`;

const WA_OPTIONS = [
  {
    type: "online",
    icon: Video,
    title: "Online Consultation",
    desc: "Video or phone consultation with Dr. Ankur Lal — from the privacy of your home.",
    message:
      "Hello Dr. Ankur Lal, I would like to book an Online Consultation (₹900) at Delhi STD Clinic. Please share the available time slots. My name is: ",
    testid: "wa-book-online",
  },
  {
    type: "clinic",
    icon: Building2,
    title: "Clinic Appointment",
    desc: "In-person consultation at the clinic in Delhi — private and by appointment.",
    message:
      "Hello Dr. Ankur Lal, I would like to book a Clinic Appointment (₹900) at Delhi STD Clinic. Please share the available time slots. My name is: ",
    testid: "wa-book-clinic",
  },
  {
    type: "hiv-pep",
    icon: AlertTriangle,
    title: "Urgent HIV PEP",
    desc: "Time-sensitive — PEP should start as soon as possible, generally within 72 hours of exposure.",
    message:
      "URGENT — Hello Dr. Ankur Lal, I may need an HIV PEP consultation. My possible exposure was on (date/time): _____. Please respond as soon as possible.",
    urgent: true,
    testid: "wa-book-pep",
  },
];

const Booking = () => {
  const [params] = useSearchParams();
  const selected = ["online", "clinic", "hiv-pep"].includes(params.get("type"))
    ? params.get("type")
    : null;

  return (
    <>
      <SEO
        title="Book on WhatsApp | Delhi STD Clinic — Dr. Ankur Lal"
        description="Book your confidential consultation with Dr. Ankur Lal directly on WhatsApp — online, clinic or urgent HIV PEP consultation. One tap, details pre-filled."
        path="/booking"
      />

      <section className="bg-[#FAF9F6] texture-grain pt-32 sm:pt-40 pb-16 sm:pb-24" data-testid="booking-page">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <Reveal className="max-w-2xl">
            <Eyebrow>Confidential Appointment</Eyebrow>
            <h1 className="mt-4 font-serif text-4xl sm:text-5xl font-semibold leading-[1.1] tracking-tight text-slate-950">
              Book Instantly on WhatsApp
            </h1>
            <p className="mt-5 text-slate-600 leading-relaxed">
              The fastest way to book with Dr. Ankur Lal. Choose your consultation type —
              WhatsApp opens with your message already written. Just add your name and tap
              send. Private, discreet, no app or account needed beyond WhatsApp.
            </p>
            <p className="mt-4 inline-flex items-center gap-2 text-sm text-slate-600">
              <IndianRupee className="w-4 h-4 text-emerald-900" />
              <span className="font-semibold text-slate-900">₹900</span> per consultation — online or clinic
            </p>
          </Reveal>

          <div className="mt-10 grid sm:grid-cols-3 gap-5">
            {WA_OPTIONS.map((o, i) => (
              <Reveal key={o.type} delay={i * 0.08}>
                <a
                  href={waLink(o.message)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={o.testid}
                  className={`group flex flex-col h-full rounded-3xl p-7 border transition-all duration-300 hover:-translate-y-1.5 ${
                    o.urgent
                      ? "bg-amber-50 border-amber-300/70 hover:shadow-[0_20px_50px_-20px_rgba(180,83,9,0.4)]"
                      : "bg-white border-slate-900/10 hover:border-emerald-700/40 hover:shadow-[0_20px_50px_-20px_rgba(6,78,59,0.3)]"
                  } ${selected === o.type ? "ring-2 ring-emerald-700 ring-offset-2" : ""}`}
                >
                  <span
                    className={`w-12 h-12 rounded-2xl flex items-center justify-center ${
                      o.urgent ? "bg-amber-600" : "bg-emerald-900"
                    }`}
                  >
                    <o.icon className="w-5 h-5 text-white" />
                  </span>
                  <h2 className="mt-5 font-serif text-2xl font-semibold text-slate-950">{o.title}</h2>
                  <p className="mt-2.5 text-sm text-slate-600 leading-relaxed flex-1">{o.desc}</p>
                  <span
                    className={`mt-6 inline-flex items-center justify-center gap-2 font-semibold text-sm px-6 py-3 rounded-full transition-all ${
                      o.urgent
                        ? "bg-amber-600 text-white group-hover:bg-amber-500"
                        : "bg-emerald-600 text-white group-hover:bg-emerald-500"
                    }`}
                  >
                    <MessageSquare className="w-4 h-4" /> Book on WhatsApp
                  </span>
                </a>
              </Reveal>
            ))}
          </div>

          {selected === "hiv-pep" && (
            <Reveal className="mt-6">
              <div className="bg-amber-50 border border-amber-300/60 rounded-2xl p-5" data-testid="booking-pep-banner">
                <p className="text-sm text-amber-950 leading-relaxed font-medium">
                  HIV PEP is time-sensitive and generally not recommended after 72 hours.
                  If your exposure was recent, tap "Urgent HIV PEP" above — or call the
                  clinic now.
                </p>
              </div>
            </Reveal>
          )}

        </div>
      </section>
    </>
  );
};

export default Booking;
